import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ChevronDown, ImagePlus, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import api, { mediaUrl } from "../../lib/api";
import { SECTIONS } from "../../lib/contentSchema";
import { useContent } from "../../context/SiteContentContext";
import { errorMessage } from "../../lib/stays";

const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const inputCls = "mt-1 bg-[#0f0f0f] border-white/10 h-10";

/** Image picker: preview + upload from the computer (or paste a link). */
function ImageField({ value, onChange, label }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Please choose an image under 5 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/site/upload", fd);
      onChange(data.url);
      toast.success("Image uploaded - remember to save");
    } catch (err) { toast.error(errorMessage(err, "Couldn't upload that image")); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-start gap-3 mt-1">
      {value ? <img src={mediaUrl(value)} alt={label || "Preview"} className="w-24 h-24 object-cover border border-white/10" />
        : <div className="w-24 h-24 border border-white/10 flex items-center justify-center text-[10px] text-white/30">No image</div>}
      <div className="flex-1 min-w-[180px] space-y-2">
        <label className={`inline-flex items-center gap-2 px-3 py-2 border border-gold/40 text-gold text-xs uppercase tracking-[0.15em] cursor-pointer ${busy ? "opacity-50 pointer-events-none" : "hover:bg-gold/10"}`}>
          <ImagePlus size={14} /> {busy ? "Uploading…" : "Upload image"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={upload} aria-label={`Upload ${label || "image"}`} />
        </label>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value.trim())} placeholder="…or paste an image link" className="bg-[#0f0f0f] border-white/10 h-9 text-xs" aria-label={`${label || "Image"} link`} />
      </div>
    </div>
  );
}

function Scalar({ field, value, onChange }) {
  if (field.type === "textarea") return <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-[#0f0f0f] border-white/10 min-h-[80px]" />;
  if (field.type === "image") return <ImageField value={value} onChange={onChange} label={field.label} />;
  return <Input value={value ?? ""} onChange={(e) => onChange(field.type === "url" ? e.target.value.trim() : e.target.value)} className={inputCls} placeholder={field.type === "url" ? "https://…" : ""} />;
}

function ObjectsField({ field, value, onChange }) {
  const rows = Array.isArray(value) ? value : [];
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const move = (i, d) => { const n = [...rows]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  const blank = () => Object.fromEntries(field.fields.map((f) => [f.key, f.key === "h" ? "tall" : ""]));
  return (
    <div className="mt-2 space-y-3">
      {rows.map((row, i) => (
        <div key={i} className="border border-white/10 p-3 space-y-2" data-testid={`${field.key}-row-${i}`}>
          <div className="flex items-center justify-between text-xs text-white/40">
            <span>{field.itemLabel} {i + 1}</span>
            <span className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="p-1 disabled:opacity-30"><ArrowUp size={14} /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label="Move down" className="p-1 disabled:opacity-30"><ArrowDown size={14} /></button>
              <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label={`Remove ${field.itemLabel} ${i + 1}`} className="p-1 text-red-400"><Trash2 size={14} /></button>
            </span>
          </div>
          {field.fields.map((f) => (
            <div key={f.key}><Label className="text-[11px] text-white/40">{f.label}</Label><Scalar field={f} value={row[f.key]} onChange={(v) => set(i, f.key, v)} /></div>
          ))}
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, blank()])} className="flex items-center gap-2 text-xs text-gold" data-testid={`${field.key}-add`}><Plus size={14} /> Add {field.itemLabel.toLowerCase()}</button>
    </div>
  );
}

function Field({ field, value, onChange, customised, onReset }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs text-white/60">{field.label}{customised && <span className="ml-2 text-[10px] text-gold">edited</span>}</Label>
        {customised && <button type="button" onClick={onReset} className="flex items-center gap-1 text-[10px] text-white/40 hover:text-gold" aria-label={`Reset ${field.label} to default`}><RotateCcw size={11} /> Reset</button>}
      </div>
      {field.type === "objects" ? <ObjectsField field={field} value={value} onChange={onChange} />
        : field.type === "list" ? (
          <Textarea value={Array.isArray(value) ? value.join("\n") : ""} onChange={(e) => onChange(e.target.value.split("\n"))} className="mt-1 bg-[#0f0f0f] border-white/10 min-h-[110px]" />
        ) : <Scalar field={field} value={value} onChange={onChange} />}
    </div>
  );
}

function Section({ section, content }) {
  const [open, setOpen] = useState(false);
  const initial = useMemo(() => Object.fromEntries(section.fields.map((f) => [f.key, content.get(f.key)])), [section, content]);
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDraft(initial); }, [initial]);

  const dirty = section.fields.some((f) => !same(draft[f.key], initial[f.key]));
  const save = async () => {
    const values = {};
    section.fields.forEach((f) => {
      let v = draft[f.key];
      if (f.type === "list") v = (v || []).map((s) => s.trim()).filter(Boolean);
      if (f.type === "objects") v = (v || []).filter((r) => Object.values(r).some((x) => String(x || "").trim()));
      values[f.key] = same(v, f.default) ? null : v;           // identical to the built-in default => remove the override
    });
    setBusy(true);
    try {
      await api.put("/admin/site/content", { values });
      await content.reload();
      toast.success(`${section.title} saved`);
    } catch (e) { toast.error(errorMessage(e, "Couldn't save")); }
    setBusy(false);
  };

  return (
    <section className="glass" data-testid={`content-section-${section.id}`}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between p-5 text-left">
        <span><span className="font-display text-xl">{section.title}</span><span className="block text-xs text-white/40 mt-1">{section.help}</span></span>
        <ChevronDown className={`text-gold transition-transform ${open ? "rotate-180" : ""}`} size={18} />
      </button>
      {open && (
        <div className="px-5 pb-6 space-y-5 border-t border-white/[0.06] pt-5">
          {section.fields.map((f) => (
            <Field key={f.key} field={f} value={draft[f.key]} onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
              customised={content.overrides?.[f.key] !== undefined} onReset={() => setDraft((d) => ({ ...d, [f.key]: f.default }))} />
          ))}
          <div className="flex items-center gap-4">
            <button type="button" onClick={save} disabled={busy || !dirty} className="btn-gold disabled:opacity-40" data-testid={`save-${section.id}`}>{busy ? "Saving…" : "Save changes"}</button>
            {dirty && <span className="text-xs text-yellow-300">Unsaved changes</span>}
          </div>
        </div>
      )}
    </section>
  );
}

export default function AdminContent() {
  const content = useContent();
  return (
    <div className="space-y-4" data-testid="admin-content">
      <p className="text-sm text-white/50">Edit the text and photos of your website here. Changes go live as soon as you save. “Reset” brings back the original wording.</p>
      {SECTIONS.map((s) => <Section key={s.id} section={s} content={content} />)}
    </div>
  );
}
